import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Badge from '../components/common/Badge';
import { User, Mail, Phone, Shield, Calendar, Save, Edit2, X } from 'lucide-react';
import { formatDateTime } from '../utils/helpers';
import toast from 'react-hot-toast';

const PerfilPage = () => {
  const { user, updateMockData, mockData } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nombre: user?.nombre || '',
    email: user?.email || '',
    telefono: user?.telefono || '',
    direccion: user?.direccion || ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      // Actualizar usuario en mockData
      const updatedUsers = mockData.usuarios.map(u => 
        u.email === user.email 
          ? { ...u, ...formData }
          : u
      );
      
      await updateMockData({ usuarios: updatedUsers });
      
      // Actualizar usuario en localStorage
      const currentUser = JSON.parse(localStorage.getItem('eureka_current_user'));
      localStorage.setItem('eureka_current_user', JSON.stringify({
        ...currentUser,
        ...formData
      }));
      
      toast.success('Perfil actualizado correctamente');
      setIsEditing(false);
    } catch (error) {
      toast.error('Error al actualizar el perfil');
    }
  };

  const handleCancel = () => {
    setFormData({
      nombre: user?.nombre || '',
      email: user?.email || '',
      telefono: user?.telefono || '',
      direccion: user?.direccion || ''
    });
    setIsEditing(false);
  };

  const getRoleBadgeVariant = (role) => {
    const variants = {
      'admin': 'danger',
      'agente': 'primary',
      'vendedor': 'success',
      'tecnico': 'warning',
      'cliente': 'info'
    };
    return variants[role] || 'default';
  };

  const getRoleLabel = (role) => {
    const labels = {
      'admin': 'Administrador',
      'agente': 'Agente Comercial',
      'vendedor': 'Vendedor',
      'tecnico': 'Técnico',
      'cliente': 'Cliente'
    };
    return labels[role] || role;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mi Perfil</h1>
            <p className="text-gray-600">Gestiona tu información personal</p>
          </div>
          {!isEditing ? (
            <Button
              variant="primary"
              icon={Edit2}
              onClick={() => setIsEditing(true)}
            >
              Editar Perfil
            </Button>
          ) : (
            <div className="flex space-x-2">
              <Button
                variant="outline"
                icon={X}
                onClick={handleCancel}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                icon={Save}
                onClick={handleSave}
              >
                Guardar Cambios
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Información Principal */}
          <div className="lg:col-span-2">
            <Card title="Información Personal">
              <div className="space-y-4">
                <Input
                  label="Nombre Completo"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  disabled={!isEditing}
                  icon={User}
                />
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={true}
                  icon={Mail}
                  helperText="El email no se puede modificar"
                />
                <Input
                  label="Teléfono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  disabled={!isEditing}
                  icon={Phone}
                  placeholder="Ej: +34 600 000 000"
                />
                <Input
                  label="Dirección"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  disabled={!isEditing}
                  placeholder="Dirección completa"
                />
              </div>
            </Card>
          </div>

          {/* Información de la Cuenta */}
          <div className="space-y-6">
            <Card title="Información de la Cuenta">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rol
                  </label>
                  <Badge variant={getRoleBadgeVariant(user?.role)}>
                    {getRoleLabel(user?.role)}
                  </Badge>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>Último Acceso</span>
                    </div>
                  </label>
                  <p className="text-sm text-gray-600">
                    {user?.ultimoAcceso ? formatDateTime(user.ultimoAcceso) : 'Nunca'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <div className="flex items-center space-x-2">
                      <Shield className="w-4 h-4" />
                      <span>Estado</span>
                    </div>
                  </label>
                  <Badge variant={user?.activo ? 'success' : 'default'}>
                    {user?.activo ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
              </div>
            </Card>

            {/* Estadísticas del Usuario */}
            {user?.role !== 'cliente' && (
              <Card title="Estadísticas">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Garantías Gestionadas</span>
                    <span className="text-lg font-bold text-gray-900">
                      {mockData?.garantias?.filter(g => 
                        g.vendedorId === user?.id || g.agenteId === user?.id
                      ).length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Averías Atendidas</span>
                    <span className="text-lg font-bold text-gray-900">
                      {mockData?.averias?.filter(a => 
                        a.tecnicoId === user?.id
                      ).length || 0}
                    </span>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Seguridad */}
        <Card title="Seguridad">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Cambiar Contraseña</h3>
              <p className="text-sm text-gray-600 mb-4">
                Para cambiar tu contraseña, contacta con el administrador del sistema.
              </p>
              <Button variant="outline" disabled>
                Solicitar Cambio de Contraseña
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default PerfilPage;
